import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, or, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase.js';
import logger from '../../utils/logger';

/**
 * Live moderation queue for a set of PDFS/{Grade}/{Subject} collections.
 *
 * Only documents that need attention are fetched (unverified or reported),
 * instead of every resource in every collection.
 *
 * BUG-01: listener failures used to be swallowed as "collection may not exist
 * yet", so a permissions problem showed as "All caught up". Errors are now
 * collected and returned so the page can show them. Readiness is tracked per
 * listener, because snapshot events repeat and a plain counter could finish
 * loading before every collection had answered.
 *
 * @param {{grade: string, subject: string}[]} pairs collections to watch
 * @returns {{ items: object[], loading: boolean, errors: {[code: string]: number} }}
 */
export default function useModerationQueue(pairs) {
  const [docsByPath, setDocsByPath] = useState({});
  const [readyKeys, setReadyKeys] = useState(() => new Set());
  const [errorsByKey, setErrorsByKey] = useState({});

  // Re-subscribe only when the set of collections actually changes
  const pairsKey = pairs.map((p) => `${p.grade}/${p.subject}`).sort().join('|');

  useEffect(() => {
    setDocsByPath({});
    setReadyKeys(new Set());
    setErrorsByKey({});
    if (!pairsKey) return undefined;

    const markReady = (key) =>
      setReadyKeys((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));

    const unsubscribes = pairsKey.split('|').map((key) => {
      const [grade, subject] = key.split('/');
      const q = query(
        collection(db, 'PDFS', grade, subject),
        or(where('verified', '==', false), where('reported', '==', true))
      );

      return onSnapshot(
        q,
        (snapshot) => {
          const changes = snapshot.docChanges();
          setDocsByPath((prev) => {
            const updated = { ...prev };
            changes.forEach((change) => {
              const path = change.doc.ref.path;
              if (change.type === 'removed') {
                delete updated[path];
                return;
              }
              const data = change.doc.data();
              updated[path] = {
                ...data,
                id: change.doc.id,
                path,
                ref: change.doc.ref,
                subject: data.subject || subject,
                grade: data.grade || grade,
              };
            });
            return updated;
          });
          setErrorsByKey((prev) => {
            if (!prev[key]) return prev;
            const { [key]: _cleared, ...rest } = prev;
            return rest;
          });
          markReady(key);
        },
        (error) => {
          logger.error(`Moderation queue listener failed for ${key}:`, error);
          setErrorsByKey((prev) => ({ ...prev, [key]: error.code || 'unknown' }));
          markReady(key);
        }
      );
    });

    return () => unsubscribes.forEach((u) => u());
  }, [pairsKey]);

  const total = pairsKey ? pairsKey.split('|').length : 0;

  const errors = useMemo(() => {
    const counts = {};
    Object.values(errorsByKey).forEach((code) => {
      counts[code] = (counts[code] || 0) + 1;
    });
    return counts;
  }, [errorsByKey]);

  const items = useMemo(() => Object.values(docsByPath), [docsByPath]);

  return { items, loading: readyKeys.size < total, errors };
}
